/**
 * Super admin shell: one left nav for every super admin page, on the queue
 * grammar. Watch (Systems, Stream, Spend), Accounts (Workspaces, Inbox),
 * Configure (Feature flags, Model routing, Platform). Prospects keeps its
 * route but is off the nav.
 *
 * The workspace scope at the top is the `ws` search param, so a link carries
 * it: Systems and Stream narrow to that workspace, and every nav link keeps
 * it. Deactivated (suspended or terminated) workspaces are not offered.
 */
import { useEffect, useState } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { useAuthSession } from "../hooks/useWorkspaceRole.js";
import "./adminShell.css";

interface NavEntry {
  to: string;
  label: string;
  /** Other first path segments under /admin that light this entry. */
  also?: string[];
}

export const ADMIN_NAV: ReadonlyArray<{ group: string; entries: NavEntry[] }> = [
  {
    group: "Watch",
    entries: [
      { to: "/admin/systems", label: "Systems" },
      { to: "/admin/stream", label: "Stream" },
      { to: "/admin/spend", label: "Spend" },
    ],
  },
  {
    group: "Accounts",
    entries: [
      { to: "/admin/workspaces", label: "Workspaces" },
      { to: "/admin/inbox", label: "Inbox" },
    ],
  },
  {
    group: "Configure",
    entries: [
      { to: "/admin/feature-flags", label: "Feature flags" },
      { to: "/admin/model-routing", label: "Model routing" },
      { to: "/admin/platform", label: "Platform" },
    ],
  },
];

/** The scope's workspace id, or null for every live workspace. */
export function useAdminScope(): [string | null, (workspaceId: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const ws = params.get("ws");
  const set = (workspaceId: string | null) =>
    setParams(
      (was) => {
        const next = new URLSearchParams(was);
        if (workspaceId) next.set("ws", workspaceId);
        else next.delete("ws");
        return next;
      },
      { replace: true },
    );
  return [ws && /^[0-9a-f-]{36}$/i.test(ws) ? ws : null, set];
}

interface ScopeOption {
  id: string;
  name: string;
}

function useLiveWorkspaces(): ScopeOption[] {
  const [options, setOptions] = useState<ScopeOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/admin/workspaces?limit=200&sort=name&status=live", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: unknown) => {
        if (cancelled || !body) return;
        const rows = Array.isArray(body)
          ? body
          : ((body as { workspaces?: unknown[] }).workspaces ?? []);
        setOptions(
          (rows as Array<{ id: string; name: string; status?: string }>)
            .filter((w) => w.status !== "suspended" && w.status !== "terminated")
            .map((w) => ({ id: w.id, name: w.name })),
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return options;
}

function ScopePicker() {
  const [ws, setWs] = useAdminScope();
  const options = useLiveWorkspaces();
  const known = !ws || options.some((o) => o.id === ws);
  return (
    <label className="scope">
      <span className="label">Scope</span>
      <select
        id="admin-scope"
        value={ws ?? ""}
        onChange={(e) => setWs(e.target.value || null)}
        aria-label="Workspace scope"
      >
        <option value="">All live workspaces</option>
        {!known && ws && <option value={ws}>This workspace</option>}
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function isOn(entry: NavEntry, pathname: string): boolean {
  const head = pathname.replace(/^\/admin\/?/, "").split("/")[0] ?? "";
  const own = entry.to.replace(/^\/admin\//, "");
  return head === own || (entry.also ?? []).includes(head);
}

export function AdminNav() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const ws = params.get("ws");
  const keep = ws ? `?ws=${encodeURIComponent(ws)}` : "";
  return (
    <aside className="adminnav" aria-label="Super admin">
      <ScopePicker />
      {ADMIN_NAV.map(({ group, entries }) => (
        <nav key={group} aria-label={group}>
          <div className="label">{group}</div>
          {entries.map((entry) => (
            <NavLink
              key={entry.to}
              to={`${entry.to}${keep}`}
              className={() => (isOn(entry, pathname) ? "nav on" : "nav")}
              aria-current={isOn(entry, pathname) ? "page" : undefined}
            >
              {entry.label}
            </NavLink>
          ))}
        </nav>
      ))}
      <p className="foot">Staff only. Every act is audit-logged.</p>
    </aside>
  );
}

/** The layout route for the admin area. Superadmins only; everyone else goes home. */
export function AdminShell() {
  const session = useAuthSession();
  const outer = useOutletContext<unknown>();
  if (session && !session.user.isSuperadmin) return <Navigate to="/" replace />;
  return (
    <div className="adminshell">
      <AdminNav />
      <div className="adminpane">
        <Outlet context={outer} />
      </div>
    </div>
  );
}
